/**
 * V250/V251: Centralized Object URL Management - Enforces max 2 URLs policy
 *
 * Only TWO logical buckets are allowed to create persistent object URLs:
 * 1. Profile photos (PhotoStore) - one per user: key `profile_photo:${userId}`
 * 2. Preview (single global preview manager) - one per selected file
 *
 * All other URL.createObjectURL calls must use these wrappers.
 *
 * DEV-ONLY counters help verify behaviour in Network tab:
 * - CREATE_PROFILE_URL / REUSE_PROFILE_URL / REVOKE_PROFILE_URL
 * - CREATE_PREVIEW_URL / REUSE_PREVIEW_URL / REVOKE_PREVIEW_URL
 */

// DEV counters (not exported - visible only via console logs)
let CREATE_PROFILE_URL = 0;
let REUSE_PROFILE_URL = 0;
let REVOKE_PROFILE_URL = 0;
let CREATE_PREVIEW_URL = 0;
let REUSE_PREVIEW_URL = 0;
let REVOKE_PREVIEW_URL = 0;

// Global preview URL manager (singleton, keyed by file signature)
let currentPreviewUrl: string | null = null;
let currentPreviewSignature: string | null = null;

function getFileSignature(file: File): string {
  // Signature is stable across flows as long as the same physical file is used
  // (same name, size and lastModified).
  const name = file.name || 'unnamed';
  const size = file.size ?? 0;
  const lastModified = (file as any).lastModified ?? 0;
  return `${name}:${size}:${lastModified}`;
}

/**
 * Get or create preview object URL (max 1 per file signature).
 * - If the same file (by signature) is requested again, reuse existing URL.
 * - When a *different* file is requested, revoke the previous URL first.
 */
export function getStablePreviewUrl(file: File): string {
  const signature = getFileSignature(file);

  // If same file signature, return existing URL
  if (currentPreviewUrl && currentPreviewSignature === signature) {
    if (import.meta.env.DEV) {
      REUSE_PREVIEW_URL++;
      console.log('✅ V251 objectUrl: REUSE_PREVIEW_URL', {
        signature,
        url: currentPreviewUrl.substring(0, 50),
        REUSE_PREVIEW_URL,
      });
    }
    return currentPreviewUrl;
  }

  // Revoke previous preview URL if exists and is different file
  if (currentPreviewUrl) {
    try {
      URL.revokeObjectURL(currentPreviewUrl);
      if (import.meta.env.DEV) {
        REVOKE_PREVIEW_URL++;
        console.log('✅ V251 objectUrl: REVOKE_PREVIEW_URL (replaced)', {
          oldUrl: currentPreviewUrl.substring(0, 50),
          oldSignature: currentPreviewSignature,
          REVOKE_PREVIEW_URL,
        });
      }
    } catch {
      // ignore revoke errors
    }
    currentPreviewUrl = null;
    currentPreviewSignature = null;
  }

  // Create new preview URL for this signature
  const newUrl = URL.createObjectURL(file);
  currentPreviewUrl = newUrl;
  currentPreviewSignature = signature;

  if (import.meta.env.DEV) {
    CREATE_PREVIEW_URL++;
    const stack = new Error().stack?.split('\n').slice(1, 6).join(' -> ') || 'no stack';
    console.log('🔵 V251 objectUrl: CREATE_PREVIEW_URL', {
      fileName: file.name,
      fileSize: `${Math.round(file.size / 1024)}KB`,
      fileType: file.type,
      objectUrl: newUrl.substring(0, 50),
      signature,
      CREATE_PREVIEW_URL,
      stack,
    });
  }

  return newUrl;
}

/**
 * Backwards-compatible alias (older code uses getOrCreatePreviewUrl).
 * Internally delegates to signature-based stable preview URL.
 */
export function getOrCreatePreviewUrl(file: File): string {
  return getStablePreviewUrl(file);
}

/**
 * Revoke current preview URL (call on cancel/unmount).
 * Does NOT affect profile-photo URLs.
 */
export function revokePreviewUrl(): void {
  if (!currentPreviewUrl) return;

  try {
    URL.revokeObjectURL(currentPreviewUrl);
    if (import.meta.env.DEV) {
      REVOKE_PREVIEW_URL++;
      console.log('✅ V251 objectUrl: REVOKE_PREVIEW_URL (cleanup)', {
        url: currentPreviewUrl.substring(0, 50),
        signature: currentPreviewSignature,
        REVOKE_PREVIEW_URL,
      });
    }
  } catch {
    // ignore revoke errors
  } finally {
    currentPreviewUrl = null;
    currentPreviewSignature = null;
  }
}

/**
 * Profile photo URL manager (ONE stable URL per `profile_photo:${userId}` key).
 * The URL string remains the same as long as the *blob identity* for that key
 * does not change (same Blob reference).
 */
const profileEntriesByKey = new Map<string, { blob: Blob; url: string }>();

/**
 * Get stable profile URL for a key.
 * - If called again with the *same* Blob instance, returns existing URL (REUSE).
 * - If called with a *different* Blob, revokes old URL and creates a new one.
 *
 * IMPORTANT:
 * - This function never revokes on unmount/close.
 * - Only when you intentionally replace the blob for the same key.
 */
export function getStableProfileUrlForKey(key: string, blob: Blob): string {
  const existing = profileEntriesByKey.get(key);

  // Same blob instance → reuse URL
  if (existing && existing.blob === blob) {
    if (import.meta.env.DEV) {
      REUSE_PROFILE_URL++;
      console.log('✅ V251 objectUrl: REUSE_PROFILE_URL', {
        key,
        url: existing.url.substring(0, 50),
        REUSE_PROFILE_URL,
      });
    }
    return existing.url;
  }

  // Different blob for same key → revoke old URL before replacing
  if (existing) {
    try {
      URL.revokeObjectURL(existing.url);
      if (import.meta.env.DEV) {
        REVOKE_PROFILE_URL++;
        console.log('✅ V251 objectUrl: REVOKE_PROFILE_URL (replacing)', {
          key,
          oldUrl: existing.url.substring(0, 50),
          REVOKE_PROFILE_URL,
        });
      }
    } catch {
      // ignore revoke errors
    }
    profileEntriesByKey.delete(key);
  }

  const newUrl = URL.createObjectURL(blob);
  profileEntriesByKey.set(key, { blob, url: newUrl });

  if (import.meta.env.DEV) {
    CREATE_PROFILE_URL++;
    const stack = new Error().stack?.split('\n').slice(1, 6).join(' -> ') || 'no stack';
    console.log('🔵 V251 objectUrl: CREATE_PROFILE_URL', {
      key,
      blobSize: `${Math.round(blob.size / 1024)}KB`,
      blobType: blob.type,
      objectUrl: newUrl.substring(0, 50),
      CREATE_PROFILE_URL,
      stack,
    });
  }

  return newUrl;
}

/**
 * Backwards-compatible alias for older PhotoStore code.
 * New code should prefer getStableProfileUrlForKey for clarity.
 */
export function createProfileUrlForKey(key: string, blob: Blob): string {
  return getStableProfileUrlForKey(key, blob);
}

/**
 * Revoke profile URL for a key (called by PhotoStore.invalidate when replacing).
 * Does NOT run on viewer close/unmount - only on explicit invalidation.
 */
export function revokeProfileUrlForKey(key: string): void {
  const entry = profileEntriesByKey.get(key);
  if (!entry) return;

  try {
    URL.revokeObjectURL(entry.url);
    if (import.meta.env.DEV) {
      REVOKE_PROFILE_URL++;
      console.log('✅ V251 objectUrl: REVOKE_PROFILE_URL (invalidate)', {
        key,
        url: entry.url.substring(0, 50),
        REVOKE_PROFILE_URL,
      });
    }
  } catch {
    // ignore revoke errors
  } finally {
    profileEntriesByKey.delete(key);
  }
}

/**
 * Get existing profile URL for a key (without creating).
 */
export function getProfileUrlForKey(key: string): string | undefined {
  const entry = profileEntriesByKey.get(key);
  return entry?.url;
}

